import axios, { AxiosResponse } from 'axios';
import { ApiResponse, LoginCredentials, RegisterData, HeadacheFormData, TensionFormData, LloroFormData } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://maldolor.com/api';

// Función de delay para rate limiting
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Función de retry extremadamente persistente para evitar 429
const retryRequest = async (fn: () => Promise<any>, retries = 15): Promise<any> => {
  try {
    return await fn();
  } catch (error: any) {
    if (error.response?.status === 429 && retries > 0) {
      // Tiempos de espera más largos y progresivos: 5s, 10s, 15s, 20s, 30s, 45s, 60s...
      const baseWait = Math.min(5000 + (16 - retries) * 5000, 120000); // Máximo 2 minutos
      const jitter = Math.random() * 2000; // Añadir variación aleatoria
      const waitTime = baseWait + jitter;
      
      console.log(`Rate limited (429), esperando ${Math.round(waitTime/1000)}s antes de reintentar... (intento ${16 - retries}/15)`);
      await delay(waitTime);
      return retryRequest(fn, retries - 1);
    }
    throw error;
  }
};

// Crear instancia de axios con timeout más alto
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // 15 segundos
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token de autenticación
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  
  return config;
});

// Interceptor para manejar respuestas
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expirado o inválido
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } else if (error.response?.status === 429) {
      // Rate limiting - será manejado por retryRequest
      console.warn('Rate limiting detectado:', error.response.status);
    }
    return Promise.reject(error);
  }
);

// Auth API con retry logic
export const authAPI = {
  login: (credentials: LoginCredentials): Promise<AxiosResponse<ApiResponse>> => {
    console.log('🌐 API Service: Starting login request to:', `${API_BASE_URL}/auth/login`);
    console.log('🌐 API Service: Credentials:', credentials.email);
    return retryRequest(() => {
      console.log('🌐 API Service: Making actual request...');
      return api.post('/auth/login', credentials);
    });
  },
  
  register: (data: RegisterData): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.post('/auth/register', data)),
  
  me: (): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.get('/auth/me')),
  
  refresh: (): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.post('/auth/refresh')),
};

// Headaches API con retry logic
export const headachesAPI = {
  getAll: (params?: { limit?: number; offset?: number; startDate?: string; endDate?: string }): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.get('/headaches', { params })),
  
  getById: (id: number): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.get(`/headaches/${id}`)),
  
  create: (data: HeadacheFormData): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.post('/headaches', data)),
  
  update: (id: number, data: Partial<HeadacheFormData>): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.put(`/headaches/${id}`, data)),
  
  delete: (id: number): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.delete(`/headaches/${id}`)),
  
  getStats: (): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.get('/headaches/stats/summary')),
};

// Users API
export const usersAPI = {
  getProfile: (): Promise<AxiosResponse<ApiResponse>> =>
    api.get('/users/profile'),
  
  updateProfile: (data: { name?: string; phone?: string; follow_up_notifications?: boolean; follow_up_enabled?: boolean }): Promise<AxiosResponse<ApiResponse>> =>
    api.put('/users/profile', data),
  
  changePassword: (data: { currentPassword: string; newPassword: string }): Promise<AxiosResponse<ApiResponse>> =>
    api.put('/users/change-password', data),
  
  deleteAccount: (password: string): Promise<AxiosResponse<ApiResponse>> =>
    api.delete('/users/account', { data: { password } }),

  uploadPhoto: (photoFile: File): Promise<AxiosResponse<ApiResponse>> => {
    const formData = new FormData();
    formData.append('photo', photoFile);
    return retryRequest(() => api.post('/users/upload-photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }));
  },

  deletePhoto: (): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.delete('/users/photo')),
};

// Admin API
export const adminAPI = {
  getDashboard: (): Promise<AxiosResponse<ApiResponse>> =>
    api.get('/admin/dashboard'),
  
  getUsers: (params?: { limit?: number; offset?: number }): Promise<AxiosResponse<ApiResponse>> =>
    api.get('/admin/users', { params }),
  
  getUser: (id: number): Promise<AxiosResponse<ApiResponse>> =>
    api.get(`/admin/users/${id}`),
  
  updateUser: (id: number, data: any): Promise<AxiosResponse<ApiResponse>> =>
    api.put(`/admin/users/${id}`, data),
  
  getHeadaches: (params?: { limit?: number; offset?: number; search?: string; startDate?: string; endDate?: string; intensity?: string }): Promise<AxiosResponse<ApiResponse>> =>
    api.get('/admin/headaches', { params }),

  getTensions: (params?: { limit?: number; offset?: number; search?: string; startDate?: string; endDate?: string }): Promise<AxiosResponse<ApiResponse>> =>
    api.get('/admin/tensions', { params }),

  getLloros: (params?: { limit?: number; offset?: number; search?: string; startDate?: string; endDate?: string }): Promise<AxiosResponse<ApiResponse>> =>
    api.get('/admin/lloros', { params }),

  getActivity: (params?: { limit?: number }): Promise<AxiosResponse<ApiResponse>> =>
    api.get('/admin/activity', { params }),

  getPushSubscriptions: (): Promise<AxiosResponse<ApiResponse>> =>
    api.get('/admin/push-subscriptions'),

  deletePushSubscription: (id: number): Promise<AxiosResponse<ApiResponse>> =>
    api.delete(`/admin/push-subscriptions/${id}`),

  getIntensityReport: (params?: { startDate?: string; endDate?: string }): Promise<AxiosResponse<ApiResponse>> =>
    api.get('/admin/reports/intensity', { params }),

  deleteUser: (id: number): Promise<AxiosResponse<ApiResponse>> =>
    api.delete(`/admin/users/${id}`),

  toggleUserAdmin: (id: number): Promise<AxiosResponse<ApiResponse>> =>
    api.post(`/admin/users/${id}/toggle-admin`),

  resetUserPassword: (id: number): Promise<AxiosResponse<ApiResponse>> =>
    api.post(`/admin/users/${id}/reset-password`),
};

// Password Reset API
export const passwordResetAPI = {
  requestReset: (email: string): Promise<AxiosResponse<ApiResponse>> =>
    api.post('/password-reset/request', { email }),
  
  validateToken: (token: string, email: string): Promise<AxiosResponse<ApiResponse>> =>
    api.get('/password-reset/validate', { params: { token, email } }),
  
  resetPassword: (token: string, email: string, newPassword: string): Promise<AxiosResponse<ApiResponse>> =>
    api.post('/password-reset/reset', { token, email, newPassword }),
};

// Tensions API con retry logic
export const tensionsAPI = {
  getAll: (params?: { limit?: number; offset?: number; startDate?: string; endDate?: string }): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.get('/tensions', { params })),
  
  create: (data: TensionFormData): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.post('/tensions', data)),
  
  update: (id: number, data: TensionFormData): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.put(`/tensions/${id}`, data)),
  
  delete: (id: number): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.delete(`/tensions/${id}`)),
  
  getStats: (): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.get('/tensions/stats')),
};

// Lloros API con retry logic
export const llorosAPI = {
  canAccess: (): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.get('/lloros/can-access')),

  getAll: (params?: { limit?: number; offset?: number; startDate?: string; endDate?: string }): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.get('/lloros', { params })),

  create: (data: LloroFormData): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.post('/lloros', data)),

  update: (id: number, data: LloroFormData): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.put(`/lloros/${id}`, data)),

  delete: (id: number): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.delete(`/lloros/${id}`)),

  getStats: (): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.get('/lloros/stats')),
};

// Impersonation API
export const impersonationAPI = {
  getUsers: (params?: { page?: number; limit?: number; search?: string }): Promise<AxiosResponse<ApiResponse>> =>
    api.get('/impersonation/users', { params }),
  
  startImpersonation: (userId: string): Promise<AxiosResponse<ApiResponse>> =>
    api.post(`/impersonation/start/${userId}`),
  
  stopImpersonation: (): Promise<AxiosResponse<ApiResponse>> =>
    api.post('/impersonation/stop'),
};

// Reminders API
export const remindersAPI = {
  getAll: (): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.get('/reminders')),
  
  create: (data: any): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.post('/reminders', data)),
  
  update: (id: number, data: any): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.put(`/reminders/${id}`, data)),
  
  delete: (id: number): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.delete(`/reminders/${id}`)),
  
  toggle: (id: number): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.patch(`/reminders/${id}/toggle`)),
  
  markAsSent: (id: number): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.patch(`/reminders/${id}/sent`)),
  
  sendEmail: (id: number): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.patch(`/reminders/${id}/send-email`)),
};

// Preferences API
export const preferencesAPI = {
  getAll: (): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.get('/preferences')),
  
  get: (key: string): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.get(`/preferences/${key}`)),
  
  set: (key: string, value: string): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.put(`/preferences/${key}`, { value })),
  
  setMultiple: (preferences: Record<string, string>): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.put('/preferences', { preferences })),
  
  delete: (key: string): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.delete(`/preferences/${key}`)),
};


// Biometric API
export const biometricAPI = {
  getStatus: (): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.get('/biometric/status')),
  
  getDevices: (): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.get('/biometric/devices')),
  
  registerBegin: (): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.post('/biometric/register/begin')),
  
  registerComplete: (data: { credential: any; deviceName?: string }): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.post('/biometric/register/complete', data)),
  
  authenticateBegin: (email: string): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.post('/biometric/authenticate/begin', { email })),
  
  authenticateComplete: (data: { email: string; credential: any }): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.post('/biometric/authenticate/complete', data)),
  
  removeDevice: (deviceId: number): Promise<AxiosResponse<ApiResponse>> =>
    retryRequest(() => api.delete(`/biometric/devices/${deviceId}`)),
};

export default api;